/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DeviceModel } from "../../device";
import { Log } from "../../logging";
import type { ITivoPlusAdIdentitySource } from "./ITivoPlusAdIdentitySource";
import { ZERO_UUID } from "./TivoPlusPlaceholders";

/**
 * Interim advertising and privacy values for the web client.
 *
 * **US Privacy, GDPR and GDPR consent remain provisional** — see
 * {@link ITivoPlusAdIdentitySource} for why the web values are not yet
 * defined. Advertising identifier and limit-ad-tracking are backed by real
 * shell-reported values on TV devices; the defensible defaults below still
 * apply everywhere else:
 *
 * - **Advertising identifier** — on a TV device, the shell-reported
 *   advertising ID ({@link TvProperties.getAdvertisingId}), falling back to
 *   the device's HSN ({@link TvProperties.getHsn}) when the shell hasn't
 *   reported one. In browser mode, and when neither value is available, the
 *   all-zero UUID: the specification mandates it when no identifier is
 *   available, and it must never be blank — a blank value still plays but
 *   silently breaks ad targeting, which is exactly the defect this rule
 *   exists to prevent.
 * - **Limit ad tracking** — the inverse of the shell-reported
 *   {@link TvProperties.hasAdvertisingTrackingEnabled} on a TV device.
 *   `false` in browser mode: there is no consent surface to read there, so
 *   claiming the viewer opted out would be an invention.
 * - **US Privacy** — specification version `1`, notice `Y`, opt-out derived
 *   from limit-ad-tracking, LSPA `-`. Yields `1YN-`, or `1YY-` when tracking
 *   is limited.
 * - **GDPR / GDPR consent** — empty. The deployment is US-only, and the
 *   specification allows both to be blank there.
 *
 * When the specification closes for the remaining provisional values,
 * replace this class — the interface keeps the change to this one file.
 */
export class DefaultAdIdentitySource implements ITivoPlusAdIdentitySource {
  private static readonly TAG: string = "DefaultAdIdentitySource";

  /** Whether the missing-identifier error has already been reported. */
  private mHasReportedMissingAdvertisingId: boolean = false;

  public getAdvertisingId(): string {
    if (DeviceModel.isTvDevice()) {
      const tvProperties = DeviceModel.getTVProperties();

      const advertisingId = tvProperties.getAdvertisingId();
      if (advertisingId !== "") {
        return advertisingId;
      }
    }

    // Reported as an error rather than a warning: playback will look fine
    // while every impression goes out untargeted, so this needs to be
    // visible without anyone noticing a playback symptom first.
    if (!this.mHasReportedMissingAdvertisingId) {
      this.mHasReportedMissingAdvertisingId = true;

      Log.w(
        DefaultAdIdentitySource.TAG,
        "no advertising identifier is available; sending the all-zero UUID, so TiVo+ impressions will not be targeted"
      );
    }

    return ZERO_UUID;
  }

  public isLimitAdTracking(): boolean {
    return (
      !DeviceModel.getTVProperties().hasAdvertisingTrackingEnabled() || false
    );
  }

  public getUsPrivacy(): string {
    const optOutOfSale = this.isLimitAdTracking() ? "Y" : "N";

    return "1Y" + optOutOfSale + "-";
  }

  public getGdpr(): string {
    return "";
  }

  public getGdprConsent(): string {
    return "";
  }

  /** Resets one-time logging. Used by tests. */
  public resetLogState(): void {
    this.mHasReportedMissingAdvertisingId = false;
  }
}
